<template>
  <div class="space-y-2">
    <div v-if="showToolbar" class="toolbar-wrapper">
      <div class="toolbar">
        <ButtonGroup>
          <Button
            variant="text"
            @click="cmd('toggleBold')"
            :severity="isActive('bold') ? 'secondary' : undefined"
            v-tooltip.bottom="'Bold'"
          >
            <span class="format-symbol">B</span>
          </Button>
          <Button
            variant="text"
            @click="cmd('toggleItalic')"
            :severity="isActive('italic') ? 'secondary' : undefined"
            v-tooltip.bottom="'Italic'"
          >
            <span class="format-symbol italic">I</span>
          </Button>
          <Button
            variant="text"
            @click="cmd('toggleUnderline')"
            :severity="isActive('underline') ? 'secondary' : undefined"
            v-tooltip.bottom="'Underline'"
          >
            <span class="format-symbol underline">U</span>
          </Button>
          <Button icon="pi pi-link" variant="text" @click="addLink" v-tooltip.bottom="'Add Link'" />
          <Button
            icon="pi pi-table"
            variant="text"
            @click="toggleTableMenu"
            v-tooltip.bottom="'Table Options'"
            aria-haspopup="true"
            aria-controls="table_menu"
          />
        </ButtonGroup>
      </div>
    </div>

    <Menu ref="menu" id="table_menu" :model="tableMenuItems" :popup="true" />

    <div class="tableWrapper overflow-auto">
      <EditorContent :editor="editor" class="min-h-[200px] prose prose-sm max-w-none" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import Button from 'primevue/button'
import ButtonGroup from 'primevue/buttongroup'
import Menu from 'primevue/menu'

const props = withDefaults(
  defineProps<{
    modelValue: string
    placeholder?: string
    showToolbar?: boolean
  }>(),
  {
    showToolbar: true,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const editor = ref<any>(null)
const menu = ref()
const tableMenuItems = ref([
  {
    label: 'Insert Table',
    icon: 'pi pi-table',
    command: () => cmd('insertTable'),
  },
  {
    label: 'Add Row',
    icon: 'pi pi-plus',
    command: () => cmd('addRowAfter'),
  },
  {
    label: 'Add Column',
    icon: 'pi pi-plus',
    command: () => cmd('addColumnAfter'),
  },
  {
    label: 'Delete Table',
    icon: 'pi pi-trash',
    severity: 'danger',
    command: () => cmd('deleteTable'),
  },
])

onMounted(() => {
  editor.value = new Editor({
    content: props.modelValue,
    autofocus: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ autolink: true, openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    onUpdate: ({ editor: ed }: { editor: any }) => {
      emit('update:modelValue', ed.getHTML())
    },
  })
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

watch(
  () => props.modelValue,
  (value) => {
    if (editor.value && editor.value.getHTML() !== value) {
      editor.value.commands.setContent(value, false)
    }
  },
)

function cmd(action: string) {
  const chain = editor.value?.chain().focus()
  switch (action) {
    case 'toggleBold':
      chain?.toggleBold().run()
      break
    case 'toggleItalic':
      chain?.toggleItalic().run()
      break
    case 'toggleUnderline':
      chain?.toggleUnderline().run()
      break
    case 'insertTable':
      chain?.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
      break
    case 'addRowAfter':
      chain?.addRowAfter().run()
      break
    case 'addColumnAfter':
      chain?.addColumnAfter().run()
      break
    case 'deleteTable':
      chain?.deleteTable().run()
      break
  }
}

function isActive(mark: string) {
  return editor.value?.isActive(mark)
}

function addLink() {
  const url = prompt('Enter URL')
  if (url) {
    editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }
}

function toggleTableMenu(event: Event) {
  menu.value?.toggle(event)
}
</script>
<style>
strong {
  font-weight: bold;
}
</style>

<style scoped>
.tableWrapper {
  overflow-x: auto;
}

.toolbar-wrapper {
  border: 2px solid #ededed;
  border-radius: 0.375rem;
  background-color: #fafafa;
  padding: 0.5rem;
}

.toolbar {
  display: flex;
  align-items: center;
}

.format-symbol {
  font-weight: bold;
  font-size: 1rem;
}

.format-symbol.italic {
  font-style: italic;
  font-weight: normal;
}

.format-symbol.underline {
  text-decoration: underline;
  font-weight: normal;
}
</style>

<style lang="scss">
/* Basic editor styles */
.tiptap {
  @apply prose prose-sm max-w-none;
  border: 2px solid #ededed !important;
  border-radius: 0.375rem;
  padding: 0.75rem;
  min-height: 200px;
  background-color: #fafafa;

  &:focus-within {
    border-color: var(--primary) !important;
  }

  /* List styles */
  ul,
  ol {
    @apply my-4;
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;
    list-style-position: outside;

    li p {
      @apply my-1;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply font-bold text-gray-900;
    line-height: 1.1;
    margin-top: 2.5rem;
    text-wrap: pretty;
    font-family: inherit;
  }

  h1,
  h2 {
    @apply mt-8 mb-4;
  }

  h1 {
    @apply text-2xl;
  }

  h2 {
    @apply text-xl;
  }

  h3 {
    @apply text-lg;
  }

  h4,
  h5,
  h6 {
    @apply text-base;
  }

  /* Paragraph styles */
  p {
    @apply my-4;
    line-height: 1.6;
  }

  /* Code and preformatted text styles */
  code {
    @apply bg-purple-100 rounded px-1 py-0.5 text-sm text-gray-900;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  pre {
    @apply bg-gray-900 rounded-lg text-white font-mono my-6 p-4;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

    code {
      @apply bg-transparent text-inherit p-0;
    }
  }

  blockquote {
    @apply border-l-4 border-gray-300 my-6 pl-4 italic;
    font-style: italic;
  }

  hr {
    @apply border-t border-gray-200 my-8;
    border: none;
  }

  /* Table-specific styling */
  table {
    @apply w-full border-collapse my-4;
    table-layout: fixed;
    border-spacing: 0;

    td,
    th {
      @apply border border-gray-200 p-2 relative align-top;
      min-width: 1em;
      text-align: left;

      > * {
        @apply mb-0;
      }
    }

    th {
      @apply bg-gray-50 font-bold text-left;
    }

    .selectedCell:after {
      @apply bg-gray-100;
      content: '';
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      pointer-events: none;
      position: absolute;
      z-index: 2;
    }

    .column-resize-handle {
      @apply bg-purple-500;
      bottom: -2px;
      pointer-events: none;
      position: absolute;
      right: -2px;
      top: 0;
      width: 4px;
    }
  }

  .tableWrapper {
    @apply my-6 overflow-x-auto;
  }

  &.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }

  /* Links */
  a {
    @apply text-blue-600 underline;
    text-decoration: underline;

    &:hover {
      @apply text-blue-800;
    }
  }

  /* Images */
  img {
    @apply max-w-full h-auto my-4;
  }
}
</style>
